import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import api, { errorMessage } from '../services/api';
import { useAuth, isAdmin } from '../services/auth';
import { APP_NAME, LOGO, gameUrl, inviteMessage, whatsappLink } from '../brand';

const newRequestId = () =>
  (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);

const EMPTY_FORM = { name: '', phone: '', ticket_ref: '' };

// Photo réduite avant envoi (réseau mobile) ; en cas d'échec, le fichier d'origine part tel quel.
async function shrinkPhoto(file) {
  try {
    if (!file.type.startsWith('image/') || file.size < 700 * 1024 || !window.createImageBitmap) return file;
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext('2d').drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.82));
    return blob && blob.size < file.size ? new File([blob], 'ticket.jpg', { type: 'image/jpeg' }) : file;
  } catch {
    return file;
  }
}

const Header = ({ user, onLogout }) => (
  <nav className="navbar navbar-expand-lg navbar-dark sticky-top" style={{ background: '#1565c0', padding: '8px 0', position: 'relative' }}>
    <div className="container d-flex justify-content-center">
      <a className="navbar-brand fw-bold d-flex align-items-center gap-2" href="/hotesse">
        <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#ffffff', overflow: 'hidden', padding: '3px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src={LOGO} alt="Supermont" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%' }} />
        </div>
        <span style={{ fontSize: '16px' }}>{APP_NAME}</span>
      </a>
    </div>
    <div className="d-flex justify-content-end align-items-center" style={{ position: 'absolute', top: '8px', right: '16px' }}>
      <span className="text-white me-2 d-none d-md-block">
        <small style={{ fontSize: '10px', display: 'block' }}>Connecté en tant que</small>
        <strong style={{ fontSize: '13px' }}><i className="fas fa-user-tag me-1"></i>{user?.username}</strong>
      </span>
      {isAdmin(user) && (
        <Link to="/admin" className="btn btn-outline-light btn-sm me-2" title="Administration" style={{ padding: '6px 10px', fontSize: '13px' }}>
          <i className="fas fa-chart-line"></i>
        </Link>
      )}
      <button onClick={onLogout} className="btn btn-outline-light btn-sm" title="Déconnexion" style={{ padding: '6px 10px', fontSize: '13px' }}>
        <i className="fas fa-sign-out-alt"></i>
      </button>
    </div>
  </nav>
);

const IconTile = ({ icon, dark }) => (
  <div style={{ width: '36px', height: '36px', background: dark ? '#1565c0' : '#e3f2fd', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px', flexShrink: 0 }}>
    <i className={`fas ${icon}`} style={{ color: dark ? '#fff' : '#1565c0', fontSize: '14px' }}></i>
  </div>
);

const card = { borderRadius: '12px', background: '#ffffff', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' };
const labelStyle = { fontSize: 'clamp(11px, 2vw, 13px)', color: '#333' };
const inputStyle = { background: '#fff', borderColor: '#dee2e6', color: '#1a1a1a', fontSize: 'clamp(13px, 2.5vw, 16px)' };
const addon = { background: '#e3f2fd', borderColor: '#dee2e6', color: '#1565c0', padding: '10px 12px' };
const stepBtn = { background: '#e3f2fd', border: '1px solid #1565c0', color: '#1565c0', width: '40px', height: '40px', padding: 0, fontSize: '18px', borderRadius: '8px' };

export default function Hostess() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Responsable sans point de vente : choisit le PDV sur lequel il teste.
  const adminNoPdv = isAdmin(user) && !user?.pdv_id;
  const [pdvs, setPdvs] = useState([]);
  const [pdvId, setPdvId] = useState(() => {
    try { return Number(localStorage.getItem('palet_admin_pdv')) || null; } catch { return null; }
  });
  const pdvParams = adminNoPdv && pdvId ? { pdv_id: pdvId } : {};

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [quantities, setQuantities] = useState({});
  const [ticketPhoto, setTicketPhoto] = useState(null);
  const [ticketPreview, setTicketPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [qrPurchase, setQrPurchase] = useState(null);
  const fileInputRef = useRef(null);
  const requestId = useRef(newRequestId());

  const loadSessionInfo = useCallback(async () => {
    try {
      if (adminNoPdv && !pdvId) { setCtx(null); return; }
      const { data } = await api.get('/palet/hostess/context', { params: pdvParams });
      setCtx(data);
    } catch (error) {
      setMessage({ type: 'danger', text: errorMessage(error, 'Erreur lors du chargement de la session') });
    } finally {
      setLoading(false);
    }
  }, [adminNoPdv, pdvId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadSessionInfo(); }, [loadSessionInfo]);
  useEffect(() => {
    if (adminNoPdv) api.get('/palet/admin/pdvs').then((r) => setPdvs(r.data)).catch(() => {});
  }, [adminNoPdv]);
  const choosePdv = (id) => {
    setPdvId(id || null);
    resetForm();
    try { localStorage.setItem('palet_admin_pdv', id || ''); } catch { /* ignore */ }
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setQuantities({});
    setTicketPhoto(null);
    setTicketPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    requestId.current = newRequestId();
  };

  const change = (patch) => {
    setFormData((f) => ({ ...f, ...patch }));
    requestId.current = newRequestId(); // formulaire modifié = nouvelle demande
  };
  const setQty = (productId, value) => {
    setQuantities((q) => ({ ...q, [productId]: Math.max(0, value) }));
    requestId.current = newRequestId();
  };

  const perPlay = ctx?.bottles_per_play || 2;
  const totalBottles = Object.values(quantities).reduce((a, b) => a + b, 0);
  const plays = Math.min(Math.floor(totalBottles / perPlay), ctx?.max_plays_per_purchase || 10);

  const handleSubmit = async (mode) => {
    if (!formData.name.trim()) return setMessage({ type: 'danger', text: 'Veuillez remplir le nom' });
    const digits = formData.phone.replace(/\D/g, '');
    if (!digits) return setMessage({ type: 'danger', text: 'Veuillez remplir le numéro de téléphone' });
    if (!/^[267]\d{8}$/.test(digits)) {
      return setMessage({ type: 'danger', text: 'Numéro invalide. Format attendu: 6XXXXXXXX (9 chiffres) (Cameroun).' });
    }
    if (totalBottles < perPlay) {
      return setMessage({ type: 'danger', text: `Minimum ${perPlay} bouteilles Supermont 1 L (Plate ou Pétillante) requises pour participer` });
    }
    if (ctx?.require_ticket_photo && !ticketPhoto) {
      return setMessage({ type: 'danger', text: 'Veuillez prendre une photo du ticket de caisse' });
    }
    if (ctx?.require_ticket_ref && !formData.ticket_ref.trim()) {
      return setMessage({ type: 'danger', text: 'Saisissez le numéro du ticket de caisse' });
    }

    setSubmitting(true);
    setMessage({ type: '', text: '' });
    const body = new FormData();
    body.append('name', formData.name.trim());
    body.append('phone', digits);
    body.append('play_mode', mode);
    body.append('quantities', JSON.stringify(quantities));
    body.append('request_id', requestId.current); // même identifiant si l'hôtesse réessaie
    if (formData.ticket_ref.trim()) body.append('ticket_ref', formData.ticket_ref.trim());
    if (pdvParams.pdv_id) body.append('pdv_id', pdvParams.pdv_id);
    if (ticketPhoto) body.append('ticket_photo', ticketPhoto, ticketPhoto.name || 'ticket.jpg');

    try {
      const { data } = await api.post('/palet/hostess/register', body, { timeout: 60000 });
      const url = gameUrl(data.game_path);
      const name = formData.name.trim();
      loadSessionInfo();
      if (mode === 'whatsapp') {
        setMessage({ type: 'success', text: 'Participant enregistré !' });
        resetForm();
        window.location.href = whatsappLink(data.client_phone, inviteMessage(name, url));
      } else if (mode === 'qr_code') {
        setQrPurchase(data);
      } else {
        setMessage({ type: 'success', text: 'Participant enregistré ! Redirection vers le jeu...' });
        resetForm();
        setTimeout(() => navigate(data.game_path), 500);
      }
    } catch (error) {
      setMessage({ type: 'danger', text: errorMessage(error, "Erreur lors de l'enregistrement") });
      if (error.response?.status === 409) loadSessionInfo();
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseQRCode = useCallback(() => {
    setQrPurchase(null);
    resetForm();
    loadSessionInfo();
  }, [loadSessionInfo]);

  // Le QR code se ferme tout seul dès que le client a ouvert le jeu.
  useEffect(() => {
    if (!qrPurchase) return undefined;
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(`/palet/hostess/purchases/${qrPurchase.purchase_token}`);
        if (data.opened || data.played) handleCloseQRCode();
      } catch { /* on réessaie */ }
    }, 2000);
    return () => clearInterval(interval);
  }, [qrPurchase, handleCloseQRCode]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const small = await shrinkPhoto(file);
    setTicketPhoto(small);
    setTicketPreview(URL.createObjectURL(small));
    requestId.current = newRequestId();
  };

  const clearPhoto = () => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    setTicketPhoto(null);
    setTicketPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  if (loading && !(adminNoPdv && !pdvId)) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: '#f4f6f9' }}>
        <div className="text-center" style={{ color: '#1565c0' }}>
          <div className="spinner-border mb-3" role="status" style={{ width: '60px', height: '60px', borderWidth: '4px' }}></div>
          <p className="mb-0 fw-bold" style={{ color: '#333' }}>CHARGEMENT...</p>
        </div>
      </div>
    );
  }

  const pdvPicker = adminNoPdv && (
    <div className="card border-0 mb-3" style={card}>
      <div className="card-body py-2 py-md-3">
        <label className="form-label fw-semibold small" htmlFor="pdv" style={labelStyle}>
          <i className="fas fa-store me-2" style={{ color: '#1565c0' }}></i>Point de vente (mode responsable)
        </label>
        <select id="pdv" className="form-select" value={pdvId || ''} onChange={(e) => choosePdv(Number(e.target.value))} style={inputStyle}>
          <option value="">Choisir un point de vente…</option>
          {pdvs.map((p) => <option key={p.id} value={p.id}>{p.city} · {p.name}</option>)}
        </select>
      </div>
    </div>
  );

  const alertBox = message.text && (
    <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show mb-3 shadow-sm`} role="alert" style={{ fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
      <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'} me-2`}></i>
      {message.text}
      <button type="button" className="btn-close" aria-label="Fermer" onClick={() => setMessage({ type: '', text: '' })}></button>
    </div>
  );

  if ((adminNoPdv && !pdvId) || !ctx || !ctx.session) {
    return (
      <div className="min-vh-100 d-flex flex-column" style={{ background: '#f4f6f9' }}>
        <Header user={user} onLogout={handleLogout} />
        <div className="container flex-grow-1 py-3">
          {alertBox}
          {pdvPicker}
          {!(adminNoPdv && !pdvId) && (
            <div className="card border-0 shadow w-100" style={{ ...card, borderRadius: '16px' }}>
              <div className="card-body py-4 py-md-5 text-center">
                <div style={{ width: '70px', height: '70px', background: '#e3f2fd', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                  <i className="fas fa-calendar-times" style={{ fontSize: '28px', color: '#1565c0' }}></i>
                </div>
                <h3 className="mb-2 fw-bold" style={{ fontSize: 'clamp(18px, 4vw, 24px)', color: '#1a1a1a' }}>Aucune session active</h3>
                <p style={{ fontSize: 'clamp(13px, 2.5vw, 15px)', color: '#555' }} className="mb-3">
                  Il n'y a pas de session de jeu active pour votre point de vente.<br />
                  Veuillez contacter un administrateur.
                </p>
                <button onClick={loadSessionInfo} className="btn btn-lg px-4" style={{ background: '#1565c0', color: '#fff', border: 'none', fontWeight: 'bold', borderRadius: '10px' }}>
                  <i className="fas fa-sync-alt me-2"></i>Actualiser
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  const time = (iso) => new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const day = (iso) => new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  const sameDay = ctx.session.start_time && ctx.session.end_time
    && new Date(ctx.session.start_time).toDateString() === new Date(ctx.session.end_time).toDateString();

  const modeButton = (mode, icon, title, sub, style) => (
    <div className="col-4">
      <button type="button" className="btn w-100 py-2 py-md-3" disabled={submitting || !ctx.can_create}
        style={{ color: '#fff', fontSize: 'clamp(11px, 2vw, 13px)', fontWeight: 'bold', borderRadius: '10px', opacity: submitting ? 0.6 : 1, ...style }}
        onClick={() => handleSubmit(mode)}>
        <i className={`${icon} me-1`}></i>
        <div className="fw-semibold">{title}</div>
        <small style={{ fontSize: 'clamp(8px, 1.8vw, 10px)' }}>{sub}</small>
      </button>
    </div>
  );

  return (
    <div className="min-vh-100" style={{ background: '#f4f6f9' }}>
      <Header user={user} onLogout={handleLogout} />

      <div className="container py-3 py-md-4">
        {alertBox}
        {pdvPicker}

        {/* Session Info Bar */}
        <div className="card border-0 mb-3" style={card}>
          <div className="card-body py-2 py-md-3">
            <div className="row align-items-center g-2 g-md-3">
              <div className="col-12 col-md-4">
                <div className="d-flex align-items-center">
                  <IconTile icon="fa-calendar-check" dark />
                  <div>
                    <small style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#666', display: 'block' }}>Session</small>
                    <strong className="d-block" style={{ fontSize: '13px', color: '#1a1a1a' }}>{ctx.session.name}</strong>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-4">
                <div className="d-flex align-items-center">
                  <IconTile icon="fa-store" />
                  <div>
                    <small style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#666', display: 'block' }}>PDV</small>
                    <strong className="d-block" style={{ fontSize: '13px', color: '#1a1a1a' }}>{ctx.pdv?.name}</strong>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-4">
                <div className="d-flex align-items-center">
                  <IconTile icon="fa-clock" />
                  <div>
                    <small style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#666', display: 'block' }}>Horaires</small>
                    <strong className="d-block" style={{ fontSize: '11px', color: '#1a1a1a' }}>
                      {sameDay
                        ? `${time(ctx.session.start_time)} - ${time(ctx.session.end_time)}`
                        : `${day(ctx.session.start_time)} ${time(ctx.session.start_time)} → ${day(ctx.session.end_time)} ${time(ctx.session.end_time)}`}
                    </strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {!ctx.can_create && (
          <div className="alert alert-warning mb-3" style={{ fontSize: '14px' }}>
            <i className="fas fa-exclamation-triangle me-2"></i>{ctx.reason}
          </div>
        )}

        <div className="row g-3 g-md-4">
          {/* Registration Form */}
          <div className="col-12">
            <div className="card border-0" style={card}>
              <div className="card-body p-3 p-md-4">
                <div className="d-flex align-items-center mb-3">
                  <IconTile icon="fa-user-plus" dark />
                  <div>
                    <h6 className="mb-0 fw-bold" style={{ fontSize: 'clamp(14px, 2.5vw, 16px)', color: '#1a1a1a' }}>Nouveau Participant</h6>
                    <small style={{ fontSize: 'clamp(11px, 2vw, 13px)', color: '#555' }}>Enregistrez un client pour le jeu</small>
                  </div>
                </div>

                <form onSubmit={(e) => e.preventDefault()} noValidate>
                  {/* Photo Section */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold small" style={labelStyle}>
                      <i className="fas fa-camera me-2" style={{ color: '#1565c0' }}></i>1. Photo du ticket
                    </label>
                    {ticketPreview ? (
                      <div className="position-relative">
                        <div className="card border-2" style={{ borderColor: '#1565c0', background: '#f0f6ff' }}>
                          <div className="card-body p-2 text-center">
                            <img src={ticketPreview} alt="Ticket" style={{ maxHeight: '150px', width: '100%', objectFit: 'contain', borderRadius: '8px' }} />
                            <div className="mt-2">
                              <span className="badge" style={{ background: '#1565c0', color: '#fff', fontSize: 'clamp(10px, 2vw, 12px)' }}>
                                <i className="fas fa-check-circle me-1"></i>Photo OK
                              </span>
                            </div>
                          </div>
                        </div>
                        <button type="button" onClick={clearPhoto} className="position-absolute top-0 end-0 btn btn-danger btn-sm shadow m-2"
                          title="Supprimer la photo" style={{ padding: '4px 8px', fontSize: '12px' }}>
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </div>
                    ) : (
                      <button type="button" onClick={() => fileInputRef.current?.click()} className="btn w-100 py-2 py-md-3 border-2"
                        style={{ background: '#f0f6ff', borderColor: '#1565c0', color: '#1565c0', fontSize: 'clamp(13px, 2.5vw, 15px)', borderRadius: '8px' }}>
                        <i className="fas fa-camera me-2"></i><span className="fw-semibold">Prendre une photo</span>
                      </button>
                    )}
                    <input ref={fileInputRef} id="ticket-photo" type="file" accept="image/*" capture="environment" onChange={handleFileSelect} style={{ display: 'none' }} />
                  </div>

                  {/* Informations du participant */}
                  <div className="row g-2 g-md-3 mb-3">
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold small" htmlFor="name" style={labelStyle}>2. Nom</label>
                      <div className="input-group">
                        <span className="input-group-text" style={addon}><i className="fas fa-user"></i></span>
                        <input id="name" type="text" className="form-control" value={formData.name} autoComplete="off"
                          onChange={(e) => change({ name: e.target.value })} placeholder="Ex: Jean Dupont" required style={inputStyle} />
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold small" htmlFor="phone" style={labelStyle}>3. Téléphone</label>
                      <div className="input-group">
                        <span className="input-group-text" style={addon}><i className="fas fa-phone"></i></span>
                        <input id="phone" type="tel" inputMode="numeric" className="form-control" placeholder="6XXXXXXXX (obligatoire)"
                          value={formData.phone} style={inputStyle}
                          onChange={(e) => {
                            let digits = (e.target.value || '').replace(/\D/g, '');
                            if (digits.startsWith('00237')) digits = digits.slice(5);
                            if (digits.startsWith('237')) digits = digits.slice(3);
                            change({ phone: digits.slice(0, 9) });
                          }} />
                      </div>
                    </div>
                  </div>

                  {/* Nombre de bouteilles */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold small" style={labelStyle}>
                      4. Nombre de bouteilles Supermont 1 L achetées
                    </label>
                    <div className="card border-0" style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '10px' }}>
                      <div className="card-body p-3">
                        <div className="row g-3">
                          {ctx.products.map((p) => {
                            const q = quantities[p.id] || 0;
                            const color = p.line === 'petillante' ? '#0b5a3b' : '#1565c0';
                            return (
                              <div key={p.id} className={ctx.products.length > 1 ? 'col-12 col-sm-6' : 'col-12'}>
                                <div className="text-center" data-product={p.line}>
                                  <div className="fw-semibold mb-2" style={{ fontSize: '13px', color }}>
                                    <i className="fas fa-bottle-water me-1"></i>{p.name}
                                    {p.format_label && <span className="text-muted fw-normal"> · {p.format_label}</span>}
                                  </div>
                                  <div className="d-flex align-items-center justify-content-center gap-3">
                                    <button type="button" className="btn btn-sm" aria-label={`Moins ${p.name}`} onClick={() => setQty(p.id, q - 1)} style={stepBtn}>
                                      <i className="fas fa-minus" style={{ fontSize: '12px' }}></i>
                                    </button>
                                    <span className="qty-value" style={{ fontSize: 'clamp(22px, 4vw, 28px)', minWidth: '40px', color: '#1a1a1a', fontWeight: 'bold' }}>{q}</span>
                                    <button type="button" className="btn btn-sm" aria-label={`Plus ${p.name}`} onClick={() => setQty(p.id, q + 1)} style={stepBtn}>
                                      <i className="fas fa-plus" style={{ fontSize: '12px' }}></i>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="mt-3 text-center">
                          <span style={{ fontSize: 'clamp(11px, 2vw, 13px)', color: '#555' }}>
                            <i className="fas fa-bullseye me-1" style={{ color: '#1565c0' }}></i>
                            {totalBottles > 0
                              ? <>{totalBottles} bouteille{totalBottles > 1 ? 's' : ''} → <strong>{plays}</strong> lancer{plays > 1 ? 's' : ''} de palet</>
                              : <>{perPlay} bouteilles 1 L (Plate ou Pétillante) = 1 lancer</>}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {ctx.require_ticket_ref && (
                    <div className="mb-3">
                      <label className="form-label fw-semibold small" htmlFor="ticket" style={labelStyle}>N° du ticket de caisse</label>
                      <div className="input-group">
                        <span className="input-group-text" style={addon}><i className="fas fa-receipt"></i></span>
                        <input id="ticket" type="text" className="form-control" autoComplete="off" autoCapitalize="characters"
                          value={formData.ticket_ref} onChange={(e) => change({ ticket_ref: e.target.value })} style={inputStyle} />
                      </div>
                    </div>
                  )}

                  <div className="row g-2 mb-3">
                    <div className="col-12">
                      <label className="form-label fw-semibold small" style={labelStyle}>5. Mode de jeu</label>
                      <div className="row g-2">
                        {modeButton('whatsapp', 'fab fa-whatsapp', 'WhatsApp', 'Envoyer au client', { background: '#25D366', border: '2px solid #25D366' })}
                        {modeButton('direct', 'fas fa-mobile-alt', 'Jouer Direct', 'Sur ce mobile', { background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)', border: '2px solid #1976d2' })}
                        {modeButton('qr_code', 'fas fa-qrcode', 'QR Code', 'Scanner', { background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)', border: '2px solid #8B5CF6' })}
                      </div>
                      {submitting && (
                        <div className="text-center mt-2" style={{ fontSize: '13px', color: '#555' }}>
                          <span className="spinner-border spinner-border-sm me-2" style={{ color: '#1565c0' }}></span>Enregistrement…
                        </div>
                      )}
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* QR Code Modal */}
      {qrPurchase && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999, padding: '20px' }}>
          <div style={{ background: '#ffffff', borderRadius: '20px', padding: '30px', maxWidth: '400px', width: '100%', textAlign: 'center', boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)' }}>
            <div style={{ width: '50px', height: '50px', background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 15px' }}>
              <i className="fas fa-qrcode" style={{ fontSize: '24px', color: '#fff' }}></i>
            </div>
            <h2 style={{ color: '#1a1a1a', marginBottom: '10px', fontSize: 'clamp(18px, 4vw, 24px)', fontWeight: 'bold' }}>QR Code de Jeu</h2>
            <p style={{ color: '#555', marginBottom: '20px', fontSize: 'clamp(13px, 2.5vw, 15px)' }}>
              Le client doit scanner ce QR code pour accéder au {APP_NAME}
              {qrPurchase.plays_total > 1 && <><br /><strong>{qrPurchase.plays_total} lancers</strong></>}
            </p>
            <div style={{ background: '#fff', padding: '20px', borderRadius: '15px', marginBottom: '20px', display: 'inline-block', border: '1px solid #dee2e6' }}>
              <QRCodeSVG value={gameUrl(qrPurchase.game_path)} size={200} level="H" includeMargin />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => window.open(gameUrl(qrPurchase.game_path), '_blank')}
                style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)', color: '#fff', border: 'none', borderRadius: '10px', padding: '12px 20px', fontSize: 'clamp(12px, 2.5vw, 14px)', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <i className="fas fa-external-link-alt"></i>Tester le lien
              </button>
              <button onClick={handleCloseQRCode}
                style={{ background: '#f4f6f9', color: '#333', border: '2px solid #dee2e6', borderRadius: '10px', padding: '12px 20px', fontSize: 'clamp(12px, 2.5vw, 14px)', fontWeight: 'bold', cursor: 'pointer' }}>
                <i className="fas fa-times me-2"></i>Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
